import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import { Box, Paper, Typography, Stack } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

export const JornadasProceso = () => {
  const { auth } = useAuth();
  const toast = useToast();

  const [jornadas, setJornadas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);

  const esAdmin = auth?.rol === "admin";
  const esGerente = auth?.rol === "gerente";

  useEffect(() => {
    if (!auth?.id) return;
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.id]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [resJornadas, resUsuarios] = await Promise.all([
        fetch(`${Global.url}jornadas/proceso`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        fetch(`${Global.url}usuarios`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
      ]);

      if (!resJornadas.ok) {
        toast.error("No se pudieron obtener las jornadas en proceso.");
        setJornadas([]);
      } else {
        const data = await resJornadas.json();
        setJornadas(Array.isArray(data) ? data : []);
      }

      if (!resUsuarios.ok) {
        toast.warning("No se pudieron cargar los usuarios.");
        setUsuarios([]);
      } else {
        const data = await resUsuarios.json();
        setUsuarios(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Error al cargar las jornadas:", error);
      toast.error("Error de conexión con el servidor.");
      setJornadas([]);
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  const getNombreCompleto = (usuario_id) => {
    const u = usuarios.find((x) => String(x.id) === String(usuario_id));
    return u ? `${u.nombre} ${u.apellidos || ""}`.trim() : `ID ${usuario_id ?? "-"}`;
  };

  const parseDateSafe = (value) => {
    if (!value) return null;

    const normalized =
      typeof value === "string" && value.includes(" ") && !value.includes("T")
        ? value.replace(" ", "T")
        : value;

    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const fmtDate = (value) => {
    const d = parseDateSafe(value);
    if (!d) return "-";

    return d.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const jornadasVisibles = useMemo(() => {
    if (esAdmin) return jornadas;

    if (esGerente) {
      const idsEquipo = new Set(
        usuarios
          .filter((u) => String(u.gerente_id) === String(auth?.id))
          .map((u) => String(u.id))
      );

      return (jornadas || []).filter((j) => idsEquipo.has(String(j.usuario_id)));
    }

    return [];
  }, [jornadas, usuarios, auth?.id, esAdmin, esGerente]);

  const rows = useMemo(
    () =>
      (jornadasVisibles || []).map((j) => ({
        ...j,
        usuario_nombre: getNombreCompleto(j.usuario_id),
        gerente_nombre: j.gerente_id ? getNombreCompleto(j.gerente_id) : "Sin gerente",
        inicio_txt: fmtDate(j.fecha_inicio),
        comentarios_txt: j.comentarios || j.comentario || "Sin comentarios",
      })),
    [jornadasVisibles, usuarios]
  );

  const columns = useMemo(() => {
    if (esAdmin) {
      return [
        { field: "usuario_nombre", headerName: "Usuario", flex: 1, minWidth: 220 },
        { field: "centro", headerName: "Centro", flex: 1, minWidth: 220 },
        { field: "inicio_txt", headerName: "Inicio", minWidth: 180 },
        { field: "gerente_nombre", headerName: "Gerente", flex: 1, minWidth: 220 },
      ];
    }

    return [
      { field: "usuario_nombre", headerName: "Usuario", flex: 1, minWidth: 220 },
      { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 200 },
      { field: "centro", headerName: "Centro", flex: 1, minWidth: 220 },
      { field: "inicio_txt", headerName: "Inicio", minWidth: 180 },
      { field: "comentarios_txt", headerName: "Comentarios", flex: 1.5, minWidth: 260 },
    ];
  }, [esAdmin]);

  const titulo = esAdmin ? "Jornadas en Proceso" : "Jornadas en Proceso de mi equipo";

  const textoSinFilas = esAdmin
    ? "No hay jornadas activas actualmente."
    : "No hay jornadas activas actualmente para tu equipo.";

  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {titulo}
        </Typography>

        <Paper sx={{ height: 640, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            loading={loading}
            pagination
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "1px solid rgba(0,0,0,0.08)",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              },
            }}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 2, color: "text.secondary" }}>
                  {loading ? "Cargando…" : textoSinFilas}
                </Box>
              ),
            }}
          />
        </Paper>
      </Stack>
    </Box>
  );
};